import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { adminApi, type AdminEnquiry, type AdminOrderListRow } from '../api/endpoints'
import { ApiError } from '../api/types'

/**
 * The v0 Orders tab — READ-ONLY (2026-09-20).
 *
 * Two lenses over the platform's deal flow:
 *   · Orders    — the `orders` collection (pre-pivot Stripe/concierge orders,
 *                 and whatever v1 brings back). No mutations: the fulfillment
 *                 board died with the v0 trim and is not sneaking back here.
 *   · Enquiries — who contacted which supplier about which stone. In a
 *                 contact-only directory this IS the live signal, which is why
 *                 it shares the tab instead of hiding in a sub-menu.
 */

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const usd = (n: number | null | undefined) =>
  (typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—')

/** Status pill tones. UNKNOWN values render the raw string in the neutral
 *  tone — a missing key must never render an empty pill (the orderStatus
 *  lesson, CLAUDE.md buyer-order-surfaces). */
const STATUS_TONE: Record<string, string> = {
  paid: 'bg-success/15 text-success',
  processing: 'bg-warning/15 text-warning',
  shipped: 'bg-navy-100 text-navy-700',
  delivered: 'bg-success/15 text-success',
  pending_payment: 'bg-warning/15 text-warning',
  cancelled: 'bg-danger/10 text-danger',
  refunded: 'bg-danger/10 text-danger',
  // Legacy broker vocabulary — still in old docs, still rendered honestly.
  requested: 'bg-warning/15 text-warning',
  confirmed: 'bg-navy-100 text-navy-700',
  sold: 'bg-success/15 text-success',
  rejected: 'bg-danger/10 text-danger',
}

function StatusPill({ status, stage }: { status: string | null; stage: string | null }) {
  const label = stage || status || 'unknown'
  const tone = STATUS_TONE[label] ?? STATUS_TONE[status ?? ''] ?? 'bg-page text-soft'
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${tone}`}>
      {label.replace(/_/g, ' ')}
    </span>
  )
}

const CHANNEL_LABEL: Record<string, string> = { call: 'Call', whatsapp: 'WhatsApp', email: 'Email' }

export function AdminOrders() {
  const [lens, setLens] = useState<'orders' | 'enquiries'>('orders')
  const [orders, setOrders] = useState<AdminOrderListRow[] | null>(null)
  const [enquiries, setEnquiries] = useState<AdminEnquiry[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    let cancelled = false
    Promise.all([adminApi.orders(), adminApi.enquiries()])
      .then(([o, e]) => {
        if (cancelled) return
        setOrders(o.orders)
        setTruncated(o.truncated)
        setEnquiries(e.items)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Could not load orders'))
      })
    return () => { cancelled = true }
  }, [])

  const needle = q.trim().toLowerCase()
  const filteredOrders = useMemo(() => (orders ?? []).filter((o) => !needle || [
    o.id, o.buyer.email, o.buyer.name, o.status, o.conciergeStage,
    ...o.stones.map((s) => s.stockId),
  ].some((v) => !!v && String(v).toLowerCase().includes(needle))), [orders, needle])
  const filteredEnquiries = useMemo(() => (enquiries ?? []).filter((e) => !needle || [
    e.tagNo, e.stockId, e.supplierName, e.channel, e.buyerUserId != null ? String(e.buyerUserId) : null,
  ].some((v) => !!v && String(v).toLowerCase().includes(needle))), [enquiries, needle])

  return (
    <div className="space-y-3" data-testid="admin-orders">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-line bg-paper p-1 w-fit" role="tablist">
          {([['orders', `Orders${orders ? ` (${orders.length})` : ''}`], ['enquiries', `Enquiries${enquiries ? ` (${enquiries.length})` : ''}`]] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={lens === k}
              onClick={() => setLens(k)}
              data-testid={`admin-orders-lens-${k}`}
              className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                lens === k ? 'text-white' : 'text-navy-500 hover:text-ink'
              }`}
            >
              {lens === k && (
                <motion.span
                  layoutId="admin-orders-lens"
                  className="absolute inset-0 rounded-full bg-navy-900"
                  transition={{ type: 'spring', stiffness: 340, damping: 34 }}
                />
              )}
              <span className="relative">{label}</span>
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={lens === 'orders' ? 'Search id, buyer, stone, status…' : 'Search stone, supplier, buyer…'}
          className="w-56 rounded-full border border-line bg-paper px-3.5 py-1.5 text-xs outline-none focus:border-navy-300"
          data-testid="admin-orders-search"
        />
        <p className="text-xs text-muted">
          Read-only. Deals close off-platform in v0 — enquiries are the live signal.
        </p>
      </div>

      {error && <div className="rounded-card border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">{error}</div>}
      {!error && (lens === 'orders' ? orders : enquiries) === null && (
        <div className="h-40 animate-pulse rounded-card bg-page" />
      )}

      {!error && lens === 'orders' && orders !== null && (
        <section className="card overflow-hidden p-0">
          {truncated && (
            <p className="border-b border-line bg-warning/10 px-4 py-2 text-[11px] text-ink">
              Showing the newest 300 orders — the scan is bounded.
            </p>
          )}
          {filteredOrders.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted">
              {needle ? 'No orders match that search.' : 'No orders yet. On-platform checkout is off in v0 — watch Enquiries for live deal flow.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-line bg-page/60 text-left">
                    {['Date', 'Order', 'Buyer', 'Stones', 'Total', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-soft">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => (
                    <tr key={o.id} className="border-b border-line last:border-b-0 align-top" data-testid={`admin-order-${o.id}`}>
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted">{fmtDate(o.orderDate)}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs text-ink" title={o.id}>{o.id.slice(0, 10)}…</span>
                        {(o.channel || o.payMethod) && (
                          <span className="mt-0.5 block text-[11px] text-muted">
                            {[o.channel, o.payMethod?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-ink">{o.buyer.name || o.buyer.email || o.buyer.uid || (o.buyer.userId != null ? `#${o.buyer.userId}` : '—')}</span>
                        {o.buyer.name && o.buyer.email && <span className="block text-[11px] text-muted">{o.buyer.email}</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-ink">{o.stones.map((s) => s.stockId).filter(Boolean).join(', ') || '—'}</span>
                        <span className="block text-[11px] text-muted">{o.pieces} pc{o.pieces === 1 ? '' : 's'}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums font-semibold text-ink">
                        {usd(o.total)}
                        {typeof o.balance === 'number' && o.balance > 0 && (
                          <span className="block text-[11px] font-normal text-warning">{usd(o.balance)} due</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5"><StatusPill status={o.status} stage={o.conciergeStage} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {!error && lens === 'enquiries' && enquiries !== null && (
        <section className="card overflow-hidden p-0">
          {filteredEnquiries.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted">
              {needle ? 'No enquiries match that search.' : 'No enquiries yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="border-b border-line bg-page/60 text-left">
                    {['Date', 'Stone', 'Supplier', 'Buyer', 'Via'].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-soft">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map((e) => (
                    <tr key={e.id} className="border-b border-line last:border-b-0" data-testid={`admin-enquiry-${e.id}`}>
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted">{fmtDate(e.createdAt)}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-ink">{e.tagNo || e.stockId || '—'}</td>
                      <td className="px-4 py-2.5 text-ink">{e.supplierName || '—'}</td>
                      <td className="px-4 py-2.5 text-muted">{e.buyerUserId != null ? String(e.buyerUserId) : '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className="rounded-full bg-page px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-500">
                          {CHANNEL_LABEL[e.channel ?? ''] ?? e.channel ?? '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
